import { useQueryClient } from "@tanstack/react-query";
import { Col, Form, message, Row, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../../../redux/store";
import { SelectOption } from "../../../../../../types/select";
import { InputField, ReactSelectField } from "../../../../../atoms/FormElement";
import { getSelectPlaceholderMessage, getValidationProps } from "../../../../../libs/form";
import { calculateTotalCharges, formatCurrency, mapToSelectOptions } from "../../../../../libs/utils";
import Button from "../../../../../molecules/Button";
import { DataDisplayRow } from "../../../../../organism";
import { DynamicFields, transformDynamicFields } from "../../../../../organism/DynamicFields";
import { getServiceProviders } from "../../../../ServiceProvider/ducks/actions";
import { getPropertyUnits } from "../../../Tickets/ducks/actions";
import { Category, CreateServiceRequest, createServiceRequest, useGetGeneralServices } from "../../api";
import { ServiceRequestFormData } from "../../types";

type ResetFieldsOnChange = {
  trigger: keyof ServiceRequestFormData;
  reset: (keyof ServiceRequestFormData)[];
};

type CreateServiceRequestFormProps = {
  onClose?: () => void;
};

export const CreateServiceRequestForm = (props: CreateServiceRequestFormProps) => {
  const { onClose } = props;

  const queryClient = useQueryClient();
  const { properties } = useSelector((state: any) => state.service);
  const propertyUnits = useSelector((state: any) => state.tickets.fetchPropertyUnits);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const form = useForm<ServiceRequestFormData>();
  const { errors } = form.formState;

  const defaultProps = { control: form.control, setValue: form.setValue };

  const watchedProperty = useWatch({ control: form.control, name: "property" });
  const watchedUnit = useWatch({ control: form.control, name: "unit" });
  const watchedRequestType = useWatch({ control: form.control, name: "requestType" });
  const watchedService = useWatch({ control: form.control, name: "category" });
  const watchedQuantity = useWatch({ control: form.control, name: "quantity" });
  const watchedDiscount = useWatch({ control: form.control, name: "discount" });

  const isPropertySelected = Boolean(watchedProperty?.value);
  const isUnitSelected = Boolean(watchedUnit?.value?.unitId);
  const requestType = watchedRequestType?.value;
  const isRequestTypeSelected = Boolean(requestType);

  const { generalServices } = useGetGeneralServices({ isEnabled: isRequestTypeSelected });
  const services: Category[] = Array.isArray(generalServices?.[requestType]) ? generalServices[requestType] : [];
  const selectedService = services.find((item) => item.categoryId === watchedService?.value);
  const serviceOptions = services.map((item) => ({
    label: item.categoryName,
    value: item.categoryId,
    isPaid: item.paid,
  }));

  const [amountWithVat, setAmountWithVat] = useState(selectedService?.amountWithVat);

  useEffect(() => {
    dispatch(getServiceProviders());
  }, []);

  useEffect(() => {
    if (isPropertySelected) dispatch(getPropertyUnits(watchedProperty?.value));
  }, [watchedProperty]);

  const resetFieldsOnChange: ResetFieldsOnChange[] = [
    { trigger: "property", reset: ["unit", "requestType", "category", "quantity", "discount"] },
    { trigger: "unit", reset: ["requestType", "category", "quantity", "discount"] },
    { trigger: "requestType", reset: ["category", "quantity", "discount"] },
    { trigger: "category", reset: ["quantity", "discount"] },
  ];

  resetFieldsOnChange.forEach(({ trigger, reset }) => {
    const watched = useWatch({ control: form.control, name: trigger });

    useEffect(() => {
      const isTruthy = typeof watched === "object" ? Boolean(watched?.value) : Boolean(watched);

      if (isTruthy) {
        reset.forEach((f) => form.resetField(f));

        if (trigger === "category") {
          const dynamicFields = form.getValues("dynamicFields") || {};
          Object.keys(dynamicFields).forEach((key) => form.resetField(`dynamicFields.${key}`));
        }
      }
    }, [watched]);
  });

  useEffect(() => {
    if (selectedService?.paid) {
      form.setValue("quantity", 1);
      form.setValue("discount", 0);
    } else {
      form.setValue("quantity", null);
      form.setValue("discount", null);
    }
  }, [selectedService]);

  useEffect(() => {
    const total = calculateTotalCharges({
      unitPrice: selectedService?.amount,
      vat: selectedService?.vat,
      quantity: watchedQuantity,
      discount: watchedDiscount,
    });

    setAmountWithVat(total);
  }, [watchedQuantity, watchedDiscount]);

  function cleanDynamicFieldsInFormData<T extends Record<string, unknown>>(formData: T): T {
    if (!formData.dynamicFields) return formData;

    const cleanedDynamicFields = Object.fromEntries(
      Object.entries(formData.dynamicFields).filter(([_, value]) => value != null),
    );

    return { ...formData, dynamicFields: cleanedDynamicFields };
  }

  const onSubmit: SubmitHandler<ServiceRequestFormData> = async (formData) => {
    const { property, unit, category, quantity, discount } = formData;

    const cleanedFormData = cleanDynamicFieldsInFormData(formData);

    const payload: CreateServiceRequest = {
      ticketType: "regular",
      ticketUserType: "landlord",
      requestFrom: "portal",
      propertyId: property.value,
      unitId: unit.value.unitId,
      category: { categoryId: category.value, name: category.label },
      ...(quantity ? { quantity: Number(quantity) } : {}),
      ...(discount ? { discount: Number(discount) } : {}),
      serviceRequestDetails: { ...transformDynamicFields(cleanedFormData) },
    };

    try {
      setIsSubmitting(true);

      await createServiceRequest(payload);
      queryClient.invalidateQueries({ queryKey: ["service-requests"] });
      onClose();
    } catch (error) {
      message.error(error?.response?.data?.error ?? "Error occurred while updating service request");
    } finally {
      setIsSubmitting(false);
    }
  };

  function addFallbackValue<T>(check: unknown, value: T, fallback: React.ReactNode = "-"): T | React.ReactNode {
    if (!check || Number.isNaN(check)) return fallback;
    return value;
  }

  return (
    <FormProvider {...form}>
      <Form
        layout="vertical"
        name="createServiceRequestForm"
        scrollToFirstError
        className="bf-main"
        onFinish={form.handleSubmit(onSubmit)}
      >
        <Row gutter={12}>
          <Col span={17}>
            <Row gutter={12} style={{ marginTop: 4 }}>
              <Col span={12}>
                <ReactSelectField
                  fieldname="property"
                  label="Property"
                  selectOption={mapToSelectOptions(properties, { labelKey: "label", valueKey: "value" })}
                  {...defaultProps}
                  {...getValidationProps({ fieldName: "property", errors, isRequired: true })}
                  iProps={{ placeholder: getSelectPlaceholderMessage("property") }}
                />
              </Col>
              <Col span={12}>
                <ReactSelectField
                  fieldname="unit"
                  label="Unit"
                  selectOption={propertyUnits?.map(({ unitId, unit }) => ({
                    label: unit.unitCode,
                    value: { ...unit, unitId },
                  }))}
                  isDisabled={!isPropertySelected}
                  {...defaultProps}
                  {...getValidationProps({ fieldName: "unit", errors, isRequired: true })}
                  iProps={{ placeholder: getSelectPlaceholderMessage("unit") }}
                />
              </Col>
            </Row>
            <Row gutter={12} style={{ marginTop: 4 }}>
              <Col span={12}>
                <ReactSelectField
                  fieldname="requestType"
                  label="Request Type"
                  selectOption={[
                    { label: "Residential", value: "residentialCategories" },
                    { label: "Commercial", value: "commercialCategories" },
                  ]}
                  isDisabled={!isUnitSelected}
                  {...defaultProps}
                  {...getValidationProps({ fieldName: "requestType", errors, isRequired: true })}
                  iProps={{ placeholder: getSelectPlaceholderMessage("requestType") }}
                />
              </Col>
              <Col span={12}>
                <ReactSelectField
                  fieldname="category"
                  label="Category"
                  selectOption={serviceOptions}
                  isDisabled={!isRequestTypeSelected}
                  {...defaultProps}
                  {...getValidationProps({ fieldName: "category", errors, isRequired: true })}
                  iProps={{ placeholder: getSelectPlaceholderMessage("category") }}
                  formatOptionLabel={(option: SelectOption & { isPaid: boolean }) => (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{option.label}</span>
                      {option?.isPaid && (
                        <Tag style={{ fontSize: "12px", padding: "2px 6px", lineHeight: "16px" }}>Paid</Tag>
                      )}
                    </div>
                  )}
                />
              </Col>
            </Row>

            {selectedService?.template && (
              <Row gutter={12}>
                <DynamicFields template={selectedService.template} {...defaultProps} errors={errors} colSpan={12} />
              </Row>
            )}
          </Col>

          <Col span={7}>
            <DataDisplayRow
              label="Unit Price"
              value={addFallbackValue(selectedService?.paid, formatCurrency(selectedService?.amount))}
            />
            <DataDisplayRow label="VAT" value={addFallbackValue(selectedService?.paid, `${selectedService?.vat}%`)} />
            <DataDisplayRow
              label="Amount (Incl. VAT)"
              value={addFallbackValue(selectedService?.paid, formatCurrency(amountWithVat))}
            />
            <Row gutter={12}>
              <Col span={12}>
                <InputField
                  className="mb-0"
                  fieldname="quantity"
                  label="Quantity"
                  control={form.control}
                  disabled={watchedQuantity == null}
                  iProps={{ type: "number", min: 1 }}
                  {...getValidationProps({ fieldName: "quantity", errors })}
                />
              </Col>
              <Col span={12}>
                <InputField
                  className="mb-0"
                  fieldname="discount"
                  label="Discount (%)"
                  control={form.control}
                  disabled={watchedDiscount == null || watchedDiscount < 0}
                  iProps={{ type: "number", min: 0, max: 100 }}
                  {...getValidationProps({ fieldName: "discount", errors })}
                />
              </Col>
            </Row>
          </Col>
        </Row>

        <Row justify="end" style={{ marginTop: "20px", gap: "0.5rem" }}>
          <Button label="Cancel" className="bf-save-draft-button" disabled={isSubmitting} onClick={onClose} />
          <Button
            type="submit"
            label={isSubmitting ? "Submitting..." : "Submit"}
            className="ef-approve-btn"
            disabled={isSubmitting}
          />
        </Row>
      </Form>
    </FormProvider>
  );
};
